import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { styled } from '@mui/material/styles';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import {
  GridCallbackDetails,
  GridPaginationModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro';

import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';

import { TripLeg } from '@/data/models/reservation.model';
import { TripLegStatus } from '@/data/models/trip-leg-status.model';
import { SortModel } from '@/data/models/sort-model.model';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';

import { ReservationEventType } from '@/services/webSocketService/updateReservation';
import { TripLegEventType } from '@/services/webSocketService/updateTripLeg';

import * as triplegSlice from '@/data/tripleg.slice';
import * as accountingSlice from '@/data/accounting.slice';
import * as lookupSlice from '@/data/lookup.slice';
import {
  fetchMonitoringTodaysTripsAsync,
  removeTripLegEventFromQueue,
  selectAllTripLegs,
  selectLoadedMonitoringTodaysTrips,
  selectLoadingMonitoringTodaysTrips,
  selectMonitoringTodaysTrips,
  selectTripLegEventQueue,
} from '@/data/slices/trip-leg.slice';

import { DataGridSearch } from '../../tables/DataGridPro';
import MonitorTodaysTripsTable from './MonitorTodaysTripsTable';
import { MonitorTripsTable } from './MonitorTripsTable';
import { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import realtimeUtils from '@/services/webSocketService/realtimeUtils';
import { DEFAULT_PAGE_SIZE } from '@/data/constants';
import { FilterModel } from '@/data/models/filters.model';
import { useDebounce } from '@uidotdev/usehooks';

const PREFIX = 'MonitorTrips';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
  filter: `${PREFIX}-filter`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(3), // Adds bottom margin to all child components except the last one
    },
    boxShadow: '0px 0px 10px #dfdfdf',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
    flexGrow: 1,
  },
  [`& .${classes.filter}`]: {
    width: 200,
  },
}));

const DEFAULT_SORT_MODEL: SortModel = {
  column: 'appointmentDate,pickUpDueTimeUTC',
  direction: 'asc,asc',
};

export default function MonitorTrips() {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const dataGridApiRef = useGridApiRef();

  const FILTER_ALL = 'ALL';
  const [currentFilter, setCurrentFilter] = useState(FILTER_ALL);
  const [searchTerm, setSearchTerm] = useState('');
  const [refetchTripLegs, setRefetchTripLegs] = useState(false);
  const debouncedRefetchTripLegs = useDebounce(refetchTripLegs, 200);
  const [sortModel, setSortModel] = useState<SortModel>(DEFAULT_SORT_MODEL);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);
  const allTripLegs = useAppSelector(selectAllTripLegs);
  const monitoringTodaysTrips = useAppSelector(selectMonitoringTodaysTrips);
  const loadingMonitoringTodaysTrips = useAppSelector(
    selectLoadingMonitoringTodaysTrips,
  );
  const loadedMonitoringTodaysTrips = useAppSelector(
    selectLoadedMonitoringTodaysTrips,
  );

  const filterOptions = [
    { label: t('triplegInventory.filterAll'), value: FILTER_ALL },
    {
      label: TripLegStatus.Draft.toUpperCase(),
      value: TripLegStatus.Draft,
    },
    {
      label: TripLegStatus.OnHold.toUpperCase(),
      value: TripLegStatus.OnHold,
    },
    {
      label: TripLegStatus.Open.toUpperCase(),
      value: TripLegStatus.Open,
    },
    {
      label: TripLegStatus.Offered.toUpperCase(),
      value: TripLegStatus.Offered,
    },
    {
      label: TripLegStatus.Accepted.toUpperCase(),
      value: TripLegStatus.Accepted,
    },
    {
      label: TripLegStatus.Confirmed.toUpperCase(),
      value: TripLegStatus.Confirmed,
    },
    { label: TripLegStatus.Onsite.toUpperCase(), value: TripLegStatus.Onsite },
    {
      label: TripLegStatus.Onboard.toUpperCase(),
      value: TripLegStatus.Onboard,
    },
    {
      label: TripLegStatus.Canceled.toUpperCase(),
      value: TripLegStatus.Canceled,
    },
    { label: TripLegStatus.NoLoad.toUpperCase(), value: TripLegStatus.NoLoad },
    {
      label: TripLegStatus.Completed.toUpperCase(),
      value: TripLegStatus.Completed,
    },
    { label: TripLegStatus.Denied.toUpperCase(), value: TripLegStatus.Denied },
    {
      label: TripLegStatus.Abandoned.toUpperCase(),
      value: TripLegStatus.Abandoned,
    },
    {
      label: TripLegStatus.InTransit.toUpperCase(),
      value: TripLegStatus.InTransit,
    },
    {
      label: TripLegStatus.CanceledLate.toUpperCase(),
      value: TripLegStatus.CanceledLate,
    },
  ];

  const activeSearchRequestRef = useRef<AbortController | null>(null);
  const fetchTripLegs = useCallback(() => {
    activeSearchRequestRef.current?.abort();
    const abortController = new AbortController();
    activeSearchRequestRef.current = abortController;

    dispatch(
      fetchMonitoringTodaysTripsAsync({
        page,
        pageSize,
        sort: sortModel?.column ?? undefined,
        sortOrder: sortModel?.direction ?? undefined,
        menu: TripLegSearchMenu.MONITORING_TODAYS_TRIPS,
        todayDate: DateTime.local().toFormat('yyyy-MM-dd'),
        search: searchTerm?.trim() || undefined,
        tripLegStatusBCs:
          currentFilter === FILTER_ALL
            ? undefined
            : [currentFilter.toUpperCase()],
        signal: abortController.signal,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, currentFilter, searchTerm]);

  useEffect(() => {
    if (debouncedRefetchTripLegs) {
      fetchTripLegs();
      setRefetchTripLegs(false);
    }
  }, [debouncedRefetchTripLegs]);

  // General refetch of page for pagination, sort, search, and filter changes.
  useEffect(() => {
    setRefetchTripLegs(true);
  }, [fetchTripLegs, page, pageSize, sortModel, searchTerm, currentFilter]);

  // We want to listen for changes to the allTripLegs adapter here, not the tripLegEventQueue
  // because it will update before the entity is updated.
  useEffect(() => {
    if (allTripLegs.length > 0 && tripLegEventQueue.length > 0) {
      const shouldRefresh = realtimeUtils.shouldRefreshTripLegData({
        tripLegEventType: tripLegEventQueue[tripLegEventQueue.length - 1]
          .detailType as TripLegEventType,
        tripLegEvent: tripLegEventQueue[tripLegEventQueue.length - 1].detail,
        tripLegs:
          monitoringTodaysTrips.items as unknown as TripLegSearchResponseItem[],
        allTripLegs: allTripLegs as unknown as TripLegSearchResponseItem[],
        filters: {
          ...monitoringTodaysTrips.filters,
          sort: sortModel.column,
          sortOrder: sortModel.direction,
        } as FilterModel,
      });
      if (shouldRefresh) {
        setRefetchTripLegs(true);
      }
      dispatch(
        removeTripLegEventFromQueue({
          detailType: tripLegEventQueue[tripLegEventQueue.length - 1]
            .detailType as TripLegEventType,
          detail: tripLegEventQueue[tripLegEventQueue.length - 1].detail,
        }),
      );
    }
  }, [allTripLegs, fetchTripLegs, dispatch]);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('passengerCareLevel'));
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  const handleTripLegFilterChange = (event: any) => {
    const f = event.target.value;
    setCurrentFilter(f);
  };

  const handleSearchChange = (value: string) => {
    const term = value?.trim() ?? '';
    const searchChanged = term !== (searchTerm?.trim() ?? '');
    setSearchTerm(value);
    if (searchChanged) {
      dispatch(triplegSlice.setMonitorPage(1));
    }
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== monitoringTodaysTrips.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const sortModelChange = (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    }
    setPage(1);
    if (newModel) {
      setSortModel(newModel);
    } else {
      setSortModel(DEFAULT_SORT_MODEL);
    }
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('monitoring.todaysTrips')}
        </Typography>
        <TextField
          select
          value={currentFilter}
          className={classes.filter}
          required={false}
          onChange={handleTripLegFilterChange}
          label={t('triplegInventory.filterStatus')}
          variant="outlined">
          {filterOptions.map((opt) => {
            return (
              <MenuItem value={opt.value} key={opt.value}>
                {opt.label}
              </MenuItem>
            );
          })}
        </TextField>
      </Box>
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        <DataGridSearch
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          placeholder={t('monitoring.searchPlaceholder')}
        />
      </Box>
      <MonitorTripsTable
        page={page}
        pageSize={pageSize}
        pageSizeOptions={[10, 20, 40, 50, 100]}
        rowCount={monitoringTodaysTrips.itemCount}
        paginationChange={paginationChange}
        sortModelChange={sortModelChange}
        tripLegs={monitoringTodaysTrips.items}
        loading={loadingMonitoringTodaysTrips}
        isSelectingTrips={false}
        showReopenAction={false}
        showInProgress={true}
        showAssignAction={false}
        showEditTimesAction={false}
        dataGridApiRef={dataGridApiRef}
      />
    </StyledBox>
  );
}
